/**
 * t374u: colour maths for per-workspace branding (WCAG 2.x relative luminance and contrast). Shared by the API
 * (validating colours an admin picks), the web app (runtime theme, Settings preview, contrast tests) and the iOS app.
 */
export type Rgb = [number, number, number];

export function parseHex(h: string | null | undefined): Rgb | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((h ?? '').trim());
  if (!m) return null;
  const s = m[1]!.length === 3 ? m[1]!.replace(/./g, c => c + c) : m[1]!;
  const n = parseInt(s, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** '#RRGGBB' lower case, or null when it isn't a 3- or 6-digit hex colour. */
export function normalizeHex(h: string | null | undefined): string | null {
  const rgb = parseHex(h);
  return rgb ? toHex(rgb) : null;
}

export const toHex = (rgb: Rgb) => `#${rgb.map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')}`;

export function luminance([r, g, b]: Rgb) {
  const c = (v: number) => { const s = v / 255; return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; };
  return 0.2126 * c(r) + 0.7152 * c(g) + 0.0722 * c(b);
}

export function contrastRatio(a: Rgb, b: Rgb) {
  const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p);
  return (x + 0.05) / (y + 0.05);
}

/** Contrast between two hex colours (1 when either is not a colour). */
export function contrastHex(a: string, b: string) {
  const x = parseHex(a), y = parseHex(b);
  return x && y ? contrastRatio(x, y) : 1;
}

/** sRGB mix: `w` of `a`, the rest `b` (what CSS `color-mix(in srgb, a w%, b)` does). */
export const mixRgb = (a: Rgb, b: Rgb, w: number): Rgb => [0, 1, 2].map(i => Math.round(a[i]! * w + b[i]! * (1 - w))) as Rgb;
export const mixHex = (a: string, b: string, w: number) => toHex(mixRgb(parseHex(a)!, parseHex(b)!, w));
/** Scale every channel (0.92 = 8% darker), as the Trade Outlet hover/pressed yellows were made. */
export const scaleHex = (a: string, f: number) => toHex(parseHex(a)!.map(v => v * f) as Rgb);

export function toHsl([r, g, b]: Rgb): [number, number, number] {
  const [R, G, B] = [r / 255, g / 255, b / 255];
  const mx = Math.max(R, G, B), mn = Math.min(R, G, B), l = (mx + mn) / 2;
  if (mx === mn) return [0, 0, l];
  const d = mx - mn, s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
  const h = mx === R ? ((G - B) / d + (G < B ? 6 : 0)) : mx === G ? (B - R) / d + 2 : (R - G) / d + 4;
  return [h * 60, s, l];
}

export function fromHsl(h: number, s: number, l: number): Rgb {
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [f(0) * 255, f(8) * 255, f(4) * 255].map(Math.round) as Rgb;
}

export const hueOf = (hex: string) => toHsl(parseHex(hex)!)[0];

/**
 * Move a colour's lightness (same hue and saturation) in small steps until `ok` holds, darker or lighter.
 * Returns the first passing colour, or the end of the range (black / white) when none does.
 */
export function shiftLightness(hex: string, dir: 'darker' | 'lighter', ok: (c: string) => boolean): string {
  const [h, s, l] = toHsl(parseHex(hex)!);
  for (let i = 1; i <= 100; i++) {
    const nl = dir === 'darker' ? l - i * 0.01 : l + i * 0.01;
    if (nl < 0 || nl > 1) break;
    const c = toHex(fromHsl(h, s, nl));
    if (ok(c)) return c;
  }
  return dir === 'darker' ? '#000000' : '#ffffff';
}

/** WCAG thresholds used everywhere: body text and non-text UI (focus rings, indicators, selected borders). */
export const AA_TEXT = 4.5;
export const AA_UI = 3;
